<template>
  <div class="billing-operation-mode">
    <operation-context-alert>
      {{ contextText }}
    </operation-context-alert>

    <operation-summary-strip :items="summaryItems" />

    <operation-panel
      :title="panelTitle"
      :subtitle="panelSubtitle"
      tone="primary"
      chip-label="Adım"
      chip-color="primary"
    >
      <template v-if="isTaxNumberOperation">
        <div class="operation-field-grid operation-field-grid--two">
          <div class="operation-field">
            <div class="operation-field__label">VKN / TCKN</div>
            <rs-text-field
              class="operation-field-input operation-field-input--compact"
              :value="financialIdentity.tax_number"
              label=""
              type="number"
              hide-details
              @input="updateFinancialIdentityField('tax_number', $event)"
            />
            <div class="operation-field__hint">
              10 veya 11 haneli numara girilir
            </div>
          </div>
          <div class="operation-field">
            <div class="operation-field__label">Profil durumu</div>
            <div class="operation-field__readonly">
              <span>{{ billingProfileStatusLabel }}</span>
            </div>
            <div class="operation-field__hint">
              {{
                missingBillingProfileFields.length
                  ? missingBillingProfileLabel
                  : "Fatura profili bilgileri hazır"
              }}
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="operation-field-grid operation-field-grid--two">
          <div class="operation-field">
            <div class="operation-field__label">Vergi dairesi</div>
            <rs-text-field
              class="operation-field-input operation-field-input--compact"
              :value="billingProfile.tax_office"
              label=""
              hide-details
              @input="updateBillingProfileField('tax_office', $event)"
            />
            <div class="operation-field__hint">Resmi fatura için zorunlu</div>
          </div>
          <div class="operation-field">
            <div class="operation-field__label">Şehir</div>
            <rs-select-location
              class="operation-field-input operation-field-input--compact"
              :value="billingProfile.city_id"
              label=""
              hide-details
              :location-type-id="2"
              @input="handleCityChange"
            />
            <div class="operation-field__hint">Müşteri şehir bilgisi</div>
          </div>
        </div>

        <div class="operation-field-grid operation-field-grid--two">
          <div class="operation-field">
            <div class="operation-field__label">İlçe</div>
            <rs-select-location
              class="operation-field-input operation-field-input--compact"
              :value="billingProfile.district_id"
              label=""
              hide-details
              :location-type-id="3"
              :parent-id="billingProfile.city_id"
              :disabled="!billingProfile.city_id"
              @input="updateBillingProfileField('district_id', $event)"
            />
            <div class="operation-field__hint">Şehir seçilince aktif olur</div>
          </div>
          <div class="operation-field">
            <div class="operation-field__label">Adres</div>
            <rs-text-field
              class="operation-field-input operation-field-input--compact"
              :value="billingProfile.address"
              label=""
              hide-details
              @input="updateBillingProfileField('address', $event)"
            />
            <div class="operation-field__hint">Müşteri fatura adresi</div>
          </div>
        </div>
      </template>
    </operation-panel>

    <operation-blocked-reasons
      v-if="blockedReasons.length"
      :reasons="blockedReasons"
      class="mb-4"
    />
  </div>
</template>

<script>
import OperationBlockedReasons from "../shared/OperationBlockedReasons.vue";
import OperationContextAlert from "../shared/OperationContextAlert.vue";
import OperationPanel from "../shared/OperationPanel.vue";
import OperationSummaryStrip from "../shared/OperationSummaryStrip.vue";

export default {
  name: "FinancialIdentityOperationMode",
  components: {
    OperationBlockedReasons,
    OperationContextAlert,
    OperationPanel,
    OperationSummaryStrip,
  },
  props: {
    draft: {
      type: Object,
      required: true,
    },
    preview: {
      type: Object,
      default: null,
    },
    operationType: {
      type: String,
      default: null,
    },
  },
  computed: {
    resolvedOperationType() {
      return this.operationType || this.preview?.operation_type || null;
    },
    isTaxNumberOperation() {
      return (
        this.resolvedOperationType === "financial_identity.update_tax_number"
      );
    },
    customer() {
      return this.preview?.customer || {};
    },
    financialIdentity() {
      return this.draft.financial_identity || {};
    },
    billingProfile() {
      return this.draft.billing_profile || {};
    },
    ownedClusters() {
      return this.preview?.owned_clusters || [];
    },
    blockedReasons() {
      return this.preview?.resolution_plan?.blocked_reasons || [];
    },
    missingBillingProfileFields() {
      return this.preview?.billing_profile?.missing_fields || [];
    },
    billingProfileStatusLabel() {
      return this.preview?.billing_profile?.is_complete ? "Hazır" : "Eksik";
    },
    missingBillingProfileLabel() {
      return `Eksik alanlar: ${this.missingBillingProfileFields.join(", ")}`;
    },
    contextText() {
      return this.isTaxNumberOperation
        ? "Resmi fatura için VKN/TCKN bilgisi gerekir. Bu adım geçmiş dönemi değiştirmez; sadece finansal kimliği tamamlar."
        : "Fatura profili, Paraşüt müşteri kaydı için gerekli resmi alanları tamamlar.";
    },
    panelTitle() {
      return this.isTaxNumberOperation
        ? "VKN / TCKN bilgisini gir"
        : "Fatura profilini tamamla";
    },
    panelSubtitle() {
      return this.isTaxNumberOperation
        ? "Kaydedilecek vergi kimliği bu müşterinin sonraki resmi fatura bilgisinde kullanılır."
        : "Vergi dairesi, adres ve konum bilgileri resmi profil için saklanır.";
    },
    summaryItems() {
      return [
        {
          label: "Müşteri",
          value: this.customer.name || "-",
        },
        {
          label: "Muhasebe ID",
          value: this.customer.accounting_id || "-",
        },
        {
          label: "Mevcut VKN / TCKN",
          value: this.customer.tax_number || "Eksik",
        },
        {
          label: "TYA kapsamı",
          value: `${this.ownedClusters.length} kayıt`,
        },
      ];
    },
  },
  methods: {
    updateFinancialIdentityField(field, value) {
      this.$emit("update-draft", {
        namespace: "financial_identity",
        field,
        value,
      });
    },
    updateBillingProfileField(field, value) {
      this.$emit("update-draft", {
        namespace: "billing_profile",
        field,
        value,
      });
    },
    handleCityChange(value) {
      this.updateBillingProfileField("city_id", value);

      if (value !== this.billingProfile.city_id) {
        this.updateBillingProfileField("district_id", null);
      }
    },
  },
};
</script>

<style src="../shared/operation-fields.css"></style>
